import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { login as loginApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Zap, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  // Load saved email if "remember me" was checked before
  useEffect(() => {
    const savedEmail = localStorage.getItem('albmove_remembered_email');
    const savedRemember = localStorage.getItem('albmove_remember_me');
    if (savedEmail && savedRemember === 'true') {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await loginApi(email, password);
      const { token, user } = response.data;
      
      // Save remember me preference
      if (rememberMe) {
        localStorage.setItem('albmove_remembered_email', email);
        localStorage.setItem('albmove_remember_me', 'true');
      } else {
        localStorage.removeItem('albmove_remembered_email');
        localStorage.removeItem('albmove_remember_me');
      }
      
      const normalizedUser = login(user, token, rememberMe);
      toast.success('Mirë se vini!', {
        description: `Jeni identifikuar si ${normalizedUser.full_name}`
      });

      navigate('/dashboard');
    } catch (err) {
      const status = err?.response?.status;
      const message = (status === 401 || status === 403 || status === 404)
        ? 'Email ose fjalëkalim i pasaktë'
        : (err.response?.data?.detail || 'Gabim në identifikim');
      setError(message);
      toast.error('Gabim', { description: message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Hero Section */}
      <div className="login-hero">
        <img
          src="https://images.unsplash.com/photo-1707758283398-7df21adba23a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHxlbGVjdHJpYyUyMHZlaGljbGUlMjBjaGFyZ2luZyUyMHN0YXRpb24lMjBuaWdodCUyMG1vZGVybiUyMGZ1dHVyaXN0aWN8ZW58MHx8fHwxNzY4NTIxMTY4fDA&ixlib=rb-4.1.0&q=85"
          alt="EV Charging Station"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="login-hero-overlay" />
        <div className="absolute bottom-12 left-12 right-12 z-10">
          <h2 className="text-4xl font-bold text-white font-outfit mb-4">
            Menaxhimi i Karikuesve Elektrikë
          </h2>
          <p className="text-lg text-zinc-300">
            Platforma enterprise për menaxhimin e infrastrukturës së karikimit të automjeteve elektrike.
          </p>
        </div>
      </div>

      {/* Login Form */}
      <div className="login-form-container">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <img 
              src="/logo-dark.png" 
              alt="AlbMove" 
              className="h-12 w-auto"
            />
          </div>

          <div className="mb-8">
            <h2 className="text-xl font-semibold text-white mb-2">Mirë se vini!</h2>
            <p className="text-zinc-400">Identifikohuni për të hyrë në panel</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div className="form-group">
              <Label htmlFor="email" className="form-label">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@shembull.com"
                className="form-input"
                required
                data-testid="login-email-input"
              />
            </div>

            <div className="form-group">
              <Label htmlFor="password" className="form-label">Fjalëkalimi</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="form-input pr-10"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="rememberMe"
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(checked)}
                className="border-zinc-600 data-[state=checked]:bg-emerald-600 data-[state=checked]:border-emerald-600"
                data-testid="remember-me-checkbox"
              />
              <Label 
                htmlFor="rememberMe" 
                className="text-sm text-zinc-400 cursor-pointer hover:text-zinc-300 transition-colors"
              >
                Më mbaj mend
              </Label>
            </div>

            <Button 
              type="submit" 
              className="w-full h-11"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Duke u identifikuar...</span>
                </div>
              ) : (
                'Identifikohu'
              )}
            </Button>
          </form>

        </div>
      </div>
    </div>
  );
};

export default Login;
